import { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { 
  ShieldCheckIcon, 
  ChartBarIcon, 
  CogIcon, 
  MegaphoneIcon,
  InformationCircleIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import {
  COOKIE_CATEGORIES,
  getConsentPreferences,
  saveConsentPreferences,
  cleanupCookiesBasedOnConsent,
  acceptAllCookies,
  rejectAllCookies
} from '../../utils/cookieConsent';
import analyticsManager from '../../utils/analyticsManager';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const categoryIcons = {
  necessary: ShieldCheckIcon,
  analytics: ChartBarIcon,
  functional: CogIcon,
  marketing: MegaphoneIcon
};

export default function PrivacyDashboard() {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(getConsentPreferences());
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Listen for consent updates
    const handleConsentUpdate = (event) => {
      setPreferences(event.detail);
    };

    window.addEventListener('consentUpdated', handleConsentUpdate);
    return () => window.removeEventListener('consentUpdated', handleConsentUpdate);
  }, []);

  const handleToggleCategory = (category) => {
    if (COOKIE_CATEGORIES[category].required) return;

    const newPreferences = {
      ...preferences,
      [category]: !preferences[category]
    };
    
    setPreferences(newPreferences);
    saveConsentPreferences(newPreferences);
    cleanupCookiesBasedOnConsent();
    
    // Update analytics manager if analytics consent changed
    if (category === 'analytics') {
      analyticsManager.updateConsent(newPreferences[category]);
    }

    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleAcceptAll = () => {
    acceptAllCookies();
    setPreferences(getConsentPreferences());
    analyticsManager.updateConsent(true);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    setPreferences(getConsentPreferences());
    analyticsManager.updateConsent(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-5 sm:px-6">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {t('Privacy.DashboardTitle', 'Privacy & Cookie Settings')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('Privacy.DashboardDescription', 'Manage your privacy preferences and cookie settings')}
        </p>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="mx-4 mt-4 rounded-md bg-green-50 p-4 sm:mx-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {t('Privacy.SettingsSaved', 'Your preferences have been saved')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="space-y-8 divide-y divide-gray-200 sm:space-y-5">
        {/* Quick Actions */}
        <div className="pt-8 sm:pt-10 px-4 sm:px-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t('Privacy.QuickActions', 'Quick Actions')}
            </h3>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAcceptAll}
              className="inline-flex justify-center rounded-md border border-transparent bg-iavisus-100 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-iavisus-200 focus:outline-none focus:ring-2 focus:ring-iavisus-100 focus:ring-offset-2"
            >
              {t('Privacy.AcceptAll', 'Accept All')}
            </button>
            <button
              onClick={handleRejectAll}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-iavisus-100 focus:ring-offset-2"
            >
              {t('Privacy.RejectAll', 'Reject All')}
            </button>
          </div>
        </div>

        {/* Cookie Categories */}
        <div className="pt-8 sm:pt-10 px-4 sm:px-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t('Privacy.CookiePreferences', 'Cookie Preferences')}
            </h3>
          </div>
          <div className="mt-6 space-y-6 sm:space-y-5">
            {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => {
              const Icon = categoryIcons[key];
              const isEnabled = preferences[key];
              
              return (
                <div key={key} className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                  <div className="flex items-center space-x-3 sm:col-span-1">
                    <Icon className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t(category.nameKey)}</div>
                      {category.required && (
                        <span className="mt-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {t('Privacy.Required', 'Required')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 sm:col-span-1 sm:mt-0">
                    <p className="text-sm text-gray-500">{t(category.descriptionKey)}</p>
                  </div>
                  <div className="mt-4 sm:col-span-1 sm:mt-0">
                    <Switch
                      checked={isEnabled}
                      onChange={() => handleToggleCategory(key)}
                      disabled={category.required}
                      className={classNames(
                        isEnabled ? 'bg-iavisus-100' : 'bg-gray-200',
                        category.required ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                        'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-iavisus-100 focus:ring-offset-2'
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={classNames(
                          isEnabled ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                        )}
                      />
                    </Switch>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info Section */}
        <div className="pt-8 sm:pt-10 px-4 sm:px-6">
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-gray-400 mt-0.5" />
            <div className="text-sm text-gray-500">
              <p>{t('Privacy.InfoText1', 'You can change your cookie preferences at any time.')}</p>
              <p className="mt-1">{t('Privacy.InfoText2', 'Disabling certain cookies may impact your experience on our website.')}</p>
              {preferences.lastUpdated && (
                <p className="mt-3 flex items-center text-xs text-gray-500">
                  <ClockIcon className="h-3 w-3 mr-1" />
                  {t('Privacy.LastUpdated', 'Last updated')}: {new Date(preferences.lastUpdated).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}